/**
 * A conversa com a Ruth — a porta principal do produto.
 *
 * Tudo que a mãe faz aqui sai pela mesma porta do backend (`channels.receber`):
 * texto, áudio e foto entram por rotas diferentes só porque o transporte é
 * diferente. Os botões "Tá certo" e "Corrigir" mandam um sim e um não como
 * qualquer outra fala — não existe atalho que grave sem passar pela conversa.
 *
 * Medidas de docs/design/Ruth.dc.html.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import {
  api,
  fraseDoErro,
  dataPorExtenso,
  diaDaCasa,
  horaDaCasa,
  type MensagemDaConversa,
} from '../api'
import { Icone } from '../componentes/Icone'

type Props = {
  /** O dia de hoje na casa, para o separador dizer "Hoje" e não a data. */
  hoje: string | null
  /** A conversa pode mudar o banco; o dia da mãe precisa saber. */
  aoMudarACasa: () => void
}

const SEM_MICROFONE = 'Não consegui usar o microfone. Me escreve?'

export function Ruth({ hoje, aoMudarACasa }: Props) {
  const [mensagens, setMensagens] = useState<MensagemDaConversa[]>([])
  const [rascunho, setRascunho] = useState('')
  const [pensando, setPensando] = useState(false)
  const [gravando, setGravando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [carregou, setCarregou] = useState(false)

  const linha = useRef<HTMLDivElement>(null)
  const gravador = useRef<MediaRecorder | null>(null)
  const seletorDeFoto = useRef<HTMLInputElement>(null)

  const carregar = useCallback(async () => {
    try {
      const { mensagens } = await api.conversa()
      setMensagens(mensagens)
      setErro(null)
    } catch (e) {
      setErro(fraseDoErro(e, 'não consegui abrir a conversa'))
    } finally {
      setCarregou(true)
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  // A conversa abre no fim, onde ela parou. `scrollIntoView` num elemento de
  // altura zero no rodapé não move nada; mexer no container move.
  useEffect(() => {
    const alvo = linha.current
    if (alvo) alvo.scrollTop = alvo.scrollHeight
  }, [mensagens, pensando])

  /** Manda por qualquer porta e recarrega: quem tem a verdade é o banco. */
  const mandar = useCallback(
    async (envio: () => Promise<unknown>) => {
      setPensando(true)
      setErro(null)
      try {
        await envio()
        await carregar()
        aoMudarACasa()
        return true
      } catch (e) {
        setErro(fraseDoErro(e, 'não consegui mandar'))
        return false
      } finally {
        setPensando(false)
      }
    },
    [carregar, aoMudarACasa],
  )

  const falar = useCallback(
    async (texto: string) => {
      const limpo = texto.trim()
      if (!limpo || pensando) return
      setRascunho('')
      // Se não foi, o que ela escreveu volta pro campo: ninguém digita duas vezes.
      if (!(await mandar(() => api.falar(limpo)))) setRascunho(texto)
    },
    [mandar, pensando],
  )

  const escolherFoto = useCallback(
    async (arquivo: File | undefined) => {
      if (!arquivo) return
      await mandar(() => api.mandarFoto(arquivo, arquivo.name))
    },
    [mandar],
  )

  const gravar = useCallback(async () => {
    if (gravando) {
      gravador.current?.stop()
      setGravando(false)
      return
    }
    try {
      const trilha = await navigator.mediaDevices.getUserMedia({ audio: true })
      const rec = new MediaRecorder(trilha)
      const pedacos: Blob[] = []
      rec.ondataavailable = (evento) => pedacos.push(evento.data)
      rec.onstop = () => {
        trilha.getTracks().forEach((t) => t.stop())
        void mandar(() => api.mandarAudio(new Blob(pedacos, { type: rec.mimeType })))
      }
      rec.start()
      gravador.current = rec
      setGravando(true)
    } catch {
      setErro(SEM_MICROFONE)
    }
  }, [gravando, mandar])

  const temTexto = rascunho.trim().length > 0

  return (
    <div className="conversa">
      <header className="conversa__cabeca">
        <span className="conversa__nome display">Ruth</span>
        <span className="conversa__papel">sua amiga da casa</span>
      </header>

      <div className="conversa__linha" ref={linha}>
        {carregou && mensagens.length === 0 && (
          <p className="conversa__comeco">
            Me conta como está o dia. Ou manda a foto de uma receita, que eu leio.
          </p>
        )}

        {mensagens.map((mensagem, i) => (
          <Fala
            key={mensagem.id}
            mensagem={mensagem}
            hoje={hoje}
            anterior={mensagens[i - 1]}
            ultima={i === mensagens.length - 1}
            pensando={pensando}
            aoResponder={falar}
          />
        ))}

        {pensando && (
          <div className="balao balao--ruth balao--pensando" aria-label="A Ruth está escrevendo">
            <span />
            <span />
            <span />
          </div>
        )}

        {erro && <p className="conversa__erro">{erro}</p>}
      </div>

      <form
        className="compositor"
        onSubmit={(evento) => {
          evento.preventDefault()
          void falar(rascunho)
        }}
      >
        <input
          ref={seletorDeFoto}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={(evento) => {
            void escolherFoto(evento.target.files?.[0])
            evento.target.value = ''
          }}
        />
        <button
          type="button"
          className="compositor__foto"
          aria-label="Mandar a foto de uma receita"
          onClick={() => seletorDeFoto.current?.click()}
          disabled={pensando || gravando}
        >
          <Icone nome="camera" tamanho={22} />
        </button>

        <input
          className="compositor__campo"
          value={rascunho}
          onChange={(evento) => setRascunho(evento.target.value)}
          placeholder={gravando ? 'Gravando…' : 'Fala ou escreve pra Ruth'}
          aria-label="Fala ou escreve pra Ruth"
          disabled={gravando}
        />

        {temTexto ? (
          <button type="submit" className="compositor__voz" aria-label="Mandar" disabled={pensando}>
            <Icone nome="enviar" tamanho={24} />
          </button>
        ) : (
          <button
            type="button"
            className={`compositor__voz ${gravando ? 'compositor__voz--gravando' : ''}`}
            aria-label={gravando ? 'Parar e mandar' : 'Falar com a Ruth'}
            onClick={() => void gravar()}
            disabled={pensando}
          >
            <Icone nome={gravando ? 'parar' : 'microfone'} tamanho={24} />
          </button>
        )}
      </form>
    </div>
  )
}

type FalaProps = {
  mensagem: MensagemDaConversa
  anterior: MensagemDaConversa | undefined
  hoje: string | null
  ultima: boolean
  pensando: boolean
  aoResponder: (texto: string) => Promise<void>
}

function Fala({ mensagem, anterior, hoje, ultima, pensando, aoResponder }: FalaProps) {
  const daMae = mensagem.de === 'mae'
  const virouODia = !anterior || diaDaCasa(anterior.quando) !== diaDaCasa(mensagem.quando)

  // O sim só aparece na última fala: uma pergunta velha já não espera resposta.
  const esperando = mensagem.espera_confirmacao && ultima && !pensando

  return (
    <>
      {virouODia && (
        <div className="conversa__dia">
          {rotuloDoDia(mensagem.quando, hoje)}, {horaDaCasa(mensagem.quando)}
        </div>
      )}

      {mensagem.tipo === 'imagem' ? (
        <div className="balao__foto">
          <Icone nome="camera" tamanho={26} />
          <span>{mensagem.texto || 'foto da receita'}</span>
        </div>
      ) : (
        <div className={`balao ${daMae ? 'balao--mae' : 'balao--ruth'}`}>
          {mensagem.tipo === 'audio' && (
            <span className="balao__audio">
              <Icone nome="microfone" tamanho={14} />
              áudio
            </span>
          )}
          {mensagem.texto}
        </div>
      )}

      {esperando && (
        <div className="confirmar">
          <button
            type="button"
            className="confirmar__sim"
            onClick={() => void aoResponder('sim')}
          >
            Tá certo
          </button>
          <button
            type="button"
            className="confirmar__nao"
            onClick={() => void aoResponder('não')}
          >
            Corrigir
          </button>
        </div>
      )}
    </>
  )
}

function rotuloDoDia(quando: string, hoje: string | null): string {
  const dia = diaDaCasa(quando)
  if (dia === hoje) return 'Hoje'
  if (hoje && dia === vespera(hoje)) return 'Ontem'
  return dataPorExtenso(dia)
}

function vespera(iso: string): string {
  const [ano, mes, dia] = iso.split('-').map(Number)
  const data = new Date(ano, mes - 1, dia - 1)
  const doisDigitos = (n: number) => String(n).padStart(2, '0')
  return `${data.getFullYear()}-${doisDigitos(data.getMonth() + 1)}-${doisDigitos(data.getDate())}`
}
